import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAnalytics } from '../../context/AnalyticsContext';
import { 
  Database, 
  Table, 
  Search, 
  RefreshCw, 
  ChevronDown, 
  ChevronRight, 
  Key, 
  Link,
  Hash,
  Type,
  Calendar,
  FileText,
  Eye,
  Filter
} from 'lucide-react';
import { AnalyticsSchemaTable, AnalyticsSchemaColumn } from '../../types';

interface SchemaExplorerProps {
  provider: 'zoho' | 'powerbi';
}

const SchemaExplorer: React.FC<SchemaExplorerProps> = ({ provider }) => {
  const { state, actions } = useAnalytics();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [columnFilter, setColumnFilter] = useState<'all' | 'primary' | 'foreign'>('all');

  const schema = state.schema[provider];
  const isLoading = state.schemaLoading;
  const error = state.schemaError;

  useEffect(() => {
    // Load schema if not already loaded
    if (!schema?.data && !isLoading && !error) {
      actions.getCachedSchema(provider);
    }
  }, [provider, schema?.data, isLoading, error]);

  const handleRefreshSchema = async () => {
    try {
      await actions.getCachedSchema(provider, true);
    } catch (error) {
      console.error('Failed to refresh schema:', error);
    }
  };

  const handleDiscoverSchema = async () => {
    try {
      await actions.discoverSchema(provider, {
        maxTables: 50,
        includeMetadata: true,
      });
    } catch (error) {
      console.error('Failed to discover schema:', error);
    }
  };

  const toggleTableExpansion = (tableName: string) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };

  const selectTable = (tableName: string) => {
    setSelectedTable(tableName);
    setShowDetails(true);
  };

  const getColumnIcon = (column: AnalyticsSchemaColumn) => {
    if (column.primaryKey) return <Key className="w-4 h-4 text-yellow-600" />;
    if (column.foreignKey) return <Link className="w-4 h-4 text-blue-600" />;
    
    const type = column.type.toLowerCase();
    if (type.includes('int') || type.includes('number') || type.includes('decimal')) {
      return <Hash className="w-4 h-4 text-green-600" />;
    }
    if (type.includes('date') || type.includes('time')) {
      return <Calendar className="w-4 h-4 text-purple-600" />;
    }
    if (type.includes('bool')) {
      return <Type className="w-4 h-4 text-red-600" />;
    }
    return <FileText className="w-4 h-4 text-gray-600" />;
  };

  const getColumnTypeColor = (type: string) => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('int') || lowerType.includes('number')) return 'text-green-600 bg-green-50';
    if (lowerType.includes('varchar') || lowerType.includes('text')) return 'text-blue-600 bg-blue-50';
    if (lowerType.includes('date') || lowerType.includes('time')) return 'text-purple-600 bg-purple-50';
    if (lowerType.includes('bool')) return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  const filterTables = (tables: AnalyticsSchemaTable[]) => {
    if (!searchTerm) return tables;
    return tables.filter(table =>
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterColumns = (columns: AnalyticsSchemaColumn[]) => {
    let filtered = columns;
    
    if (columnFilter === 'primary') {
      filtered = filtered.filter(col => col.primaryKey);
    } else if (columnFilter === 'foreign') {
      filtered = filtered.filter(col => col.foreignKey);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(col =>
        col.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        col.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const selectedTableData = selectedTable 
    ? schema?.data?.tables.find(t => t.name === selectedTable)
    : null;

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          <span>Loading schema...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <div className="text-red-600 mb-4">
            <Database className="w-12 h-12 mx-auto mb-2" />
            <h3 className="text-lg font-semibold">Schema Error</h3>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={handleDiscoverSchema}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry Discovery
          </Button>
        </div>
      </Card>
    );
  }

  if (!schema?.data) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Schema Available</h3>
          <p className="text-text-secondary mb-4">
            Discover your data structure to enable analytics conversations.
          </p>
          <Button onClick={handleDiscoverSchema}>
            <Database className="w-4 h-4 mr-2" />
            Discover Schema
          </Button>
        </div>
      </Card>
    );
  }

  const tables = schema.data.tables;
  const filteredTables = filterTables(tables);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Schema Browser */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Schema Browser</h3>
            <p className="text-sm text-text-secondary">
              {tables.length} tables discovered
              {schema.lastUpdated && (
                <span className="ml-2">
                  • Updated {new Date(schema.lastUpdated).toLocaleString()}
                </span>
              )}
            </p>
          </div>
          <Button onClick={handleRefreshSchema} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search tables and columns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tables List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredTables.map((table) => (
            <div key={table.name} className="border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleTableExpansion(table.name)}
              >
                <div className="flex items-center space-x-2">
                  {expandedTables.has(table.name) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                  <Table className="w-4 h-4 text-blue-600" />
                  <div>
                    <div className="font-medium text-sm">{table.displayName || table.name}</div>
                    <div className="text-xs text-gray-500">
                      {table.columns.length} columns • {table.rowCount.toLocaleString()} rows
                    </div>
                  </div>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    selectTable(table.name);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>

              {expandedTables.has(table.name) && (
                <div className="border-t border-gray-200 p-3 bg-gray-50">
                  {table.description && (
                    <p className="text-xs text-gray-600 mb-2">{table.description}</p>
                  )}
                  <div className="space-y-1">
                    {table.columns.slice(0, 5).map((column) => (
                      <div key={column.name} className="flex items-center space-x-2 text-xs">
                        {getColumnIcon(column)}
                        <span className="font-mono">{column.name}</span>
                        <span className={`px-1 py-0.5 rounded text-xs ${getColumnTypeColor(column.type)}`}>
                          {column.type}
                        </span>
                        {column.primaryKey && (
                          <span className="text-yellow-600">PK</span>
                        )}
                        {column.foreignKey && (
                          <span className="text-blue-600">FK</span>
                        )}
                      </div>
                    ))}
                    {table.columns.length > 5 && (
                      <div className="text-xs text-gray-500">
                        ... and {table.columns.length - 5} more columns
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredTables.length === 0 && searchTerm && (
          <div className="text-center py-4 text-gray-500">
            No tables found matching "{searchTerm}"
          </div>
        )}
      </Card>

      {/* Table Details */}
      <Card className="p-6">
        {showDetails && selectedTableData ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  {selectedTableData.displayName || selectedTableData.name}
                </h3>
                <p className="text-sm text-text-secondary">
                  {selectedTableData.columns.length} columns • {selectedTableData.rowCount.toLocaleString()} rows
                </p>
              </div>
              <Button onClick={() => setShowDetails(false)} variant="ghost" size="sm">
                ✕
              </Button>
            </div>

            {selectedTableData.description && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">{selectedTableData.description}</p>
              </div>
            )}

            {/* Column Filter */}
            <div className="flex items-center space-x-2 mb-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={columnFilter}
                onChange={(e) => setColumnFilter(e.target.value as any)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="all">All Columns</option>
                <option value="primary">Primary Keys</option>
                <option value="foreign">Foreign Keys</option>
              </select>
            </div>

            {/* Columns List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filterColumns(selectedTableData.columns).map((column) => (
                <div key={column.name} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getColumnIcon(column)}
                      <span className="font-mono font-medium">{column.name}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getColumnTypeColor(column.type)}`}>
                        {column.type}
                      </span>
                    </div>
                    <div className="flex space-x-1">
                      {column.primaryKey && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Primary Key
                        </span>
                      )}
                      {column.foreignKey && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          Foreign Key
                        </span>
                      )}
                      {!column.nullable && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                          Required
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {column.description && (
                    <p className="text-sm text-gray-600 mb-2">{column.description}</p>
                  )}
                  
                  {column.foreignKey && (
                    <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                      References: {column.foreignKey.table}.{column.foreignKey.column}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filterColumns(selectedTableData.columns).length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No columns match the current filter
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Table className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">Table Details</h3>
            <p className="text-text-secondary">
              Select a table from the schema browser to view its columns and metadata.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SchemaExplorer;